import React, { useState, useEffect, useMemo } from "react";
import { Card, Button, Form, Table, Alert, Container } from "react-bootstrap";
import { BrandTeam, initialBrandTeam } from "../../types/model";
import {
	addTeamsFromFile,
	assignBrandTeams,
	createBrandTeam,
	getBrandTeams,
	parseBrandTeams,
	syncBrandTeamLogosFromFile,
	updateBrandTeam,
	uploadBrandTeamImage,
} from "../../services/brandTeamService";
import EditBrandTeamModal from "./components/Modals/EditBrandTeamModal";
import AdminLayout from "./AdminLayout";

const AdminBrandTeamPage: React.FC = () => {
	const [teams, setTeams] = useState<BrandTeam[]>([]);
	const [loading, setLoading] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [newTeam, setNewTeam] = useState<BrandTeam>({
		name: "",
		shortName: "",
		country: "",
		sponsor: "",
		color: "",
		isActive: true,
		featured: false,
		logoUrl: "",
		source: "",
	});
	const [selectedTeam, setSelectedTeam] = useState<BrandTeam>(initialBrandTeam);
	const [message, setMessage] = useState<{
		type: "success" | "danger" | "warning";
		text: string;
	} | null>(null);
	const [logoSyncSummary, setLogoSyncSummary] = useState<any | null>(null);
	const [teamSearch, setTeamSearch] = useState("");

	const filteredTeams = useMemo(() => {
		const normalizedSearch = teamSearch.trim().toLowerCase();
		const sortedTeams = [...teams].sort((a, b) =>
			String(a.name || "").localeCompare(String(b.name || "")),
		);

		if (!normalizedSearch) {
			return sortedTeams;
		}

		return sortedTeams.filter((team) => {
			const fieldsToSearch = [
				team.name,
				team.shortName,
				team.country,
				team.sponsor,
				team.source,
			];

			return fieldsToSearch.some((field) =>
				String(field || "").toLowerCase().includes(normalizedSearch),
			);
		});
	}, [teams, teamSearch]);

	useEffect(() => {
		// Fetch existing teams from the database
		fetchTeams();
	}, []);

	const fetchTeams = async () => {
		try {
			// Replace with your API call to fetch teams
			const response = await getBrandTeams();
			const data = response.data;
			setTeams(data);
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to fetch teams" });
		}
	};

	const handleAssignRiderTeams = async () => {
		try {
			const res = await assignBrandTeams();
			if (res) {
				setMessage({
					type: "success",
					text: "Successfully assigned brand teams to riders",
				});
			}
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to assign brand teams" });
		}
	};

	const handleAddTeamsFromFile = async () => {
		try {
			const res = await addTeamsFromFile();
			if (res) {
				setMessage({
					type: "success",
					text: "Successfully added teams from file",
				});
			}
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to add teams from file" });
		}
	};

	const handleParseBrandTeams = async () => {
		setLoading(true);
		try {
			const res = await parseBrandTeams();

			setMessage({
				type: "success",
				text: "Successfully parsed brand teams from riders",
			});
			fetchTeams(); // Refresh the team list
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to parse brand teams" });
		} finally {
			setLoading(false);
		}
	};

	const handleEditInputChange = (field: keyof BrandTeam, value: any) => {
		setSelectedTeam({ ...selectedTeam, [field]: value });
	};

	// if in teams array, the imageURL changes for the selected team, update the selected team
	useEffect(() => {
		const updatedTeam = teams.find((team) => team._id === selectedTeam._id);
		if (updatedTeam) {
			setSelectedTeam({ ...selectedTeam, imageUrl: updatedTeam.imageUrl });
		}
	}, [teams]);

	const handleUpdateBrandTeam = async () => {
		setLoading(true);
		try {
			const res = await updateBrandTeam(selectedTeam._id, selectedTeam);
			if (res) {
				setMessage({
					type: "success",
					text: "Successfully updated brand team",
				});
				fetchTeams(); // Refresh the team list
			}
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to update brand team" });
		}
		setLoading(false);
	};

	const handleCloseEditModal = () => {
		setShowEditModal(false);
		setSelectedTeam(initialBrandTeam);
	};

	const createTeam = async () => {
		if (!newTeam.name) {
			setMessage({
				type: "warning",
				text: "Please fill out all required fields",
			});
			return;
		}

		try {
			// Replace with your API call to create a new team
			await createBrandTeam(newTeam);
			setMessage({ type: "success", text: "Team created successfully" });
			setNewTeam({
				name: "",
				shortName: "",
				country: "",
				sponsor: "",
				color: "",
				isActive: true,
				featured: false,
				logoUrl: "",
				source: "",
			});
			fetchTeams(); // Refresh the team list
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to create team" });
		}
	};

	const handleUploadImage = async (selectedFile: File) => {
		if (!selectedFile) return;

		try {
			const formData = new FormData();
			formData.append("image", selectedFile);

			const res = await uploadBrandTeamImage(selectedTeam?._id || "", formData);
			if (res) {
				alert("Image uploaded successfully");
				fetchTeams(); // Refresh the team list
			}
		} catch (error) {
			alert("Error uploading photo:" + error);
		}
	};

	const handleSyncTeamLogos = async (dryRun: boolean) => {
		setLoading(true);
		try {
			const res = await syncBrandTeamLogosFromFile(dryRun);
			const summary = res?.data || null;
			setLogoSyncSummary(summary);
			setMessage({
				type: "success",
				text: dryRun
					? "Logo sync dry run completed"
					: "Logo sync completed and updates were applied",
			});

			if (!dryRun) {
				await fetchTeams();
			}
		} catch (error) {
			setMessage({ type: "danger", text: "Failed to sync team logos from file" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<AdminLayout>
			<Container style={{ padding: "20px" }}>
			{message && <Alert variant={message.type}>{message.text}</Alert>}

			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Parse All Brand Teams from Riders</Card.Title>
					<Card.Text>
						This will go through all riders, make a list of their team names,
						and create objects in the database for each team name.
					</Card.Text>
					<Button
						variant="primary"
						onClick={handleParseBrandTeams}
						disabled={loading}
					>
						{loading ? "Parsing..." : "Parse Brand Teams"}
					</Button>
				</Card.Body>
			</Card>
			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Assign Rider Teams</Card.Title>
					<Card.Text>
						This will go through all teams and update the riders with the
						corresponding team ID.
					</Card.Text>
					<Button
						variant="primary"
						onClick={handleAssignRiderTeams}
						disabled={loading}
					>
						{loading ? "Assigning..." : "Assign Rider Teams"}
					</Button>
				</Card.Body>
			</Card>
			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Add New Teams from UCI File</Card.Title>
					<Card.Text>
						This will go through the file from the UCI and add any new teams to
						the brand teams table. Currently set to filter only for DH teams.
					</Card.Text>
					<Button
						variant="primary"
						onClick={handleAddTeamsFromFile}
						disabled={loading}
					>
						{loading ? "Adding..." : "Add Teams from File"}
					</Button>
				</Card.Body>
			</Card>
			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Sync Team Logos from File</Card.Title>
					<Card.Text>
						Matches normalized team names to BrandTeams and updates logo URL +
						source from <code>server/Riders/team_logos_2026_cleaned.json</code>.
					</Card.Text>
					<div className="d-flex gap-2">
						<Button
							variant="outline-primary"
							onClick={() => handleSyncTeamLogos(true)}
							disabled={loading}
						>
							{loading ? "Running..." : "Dry Run Logo Sync"}
						</Button>
						<Button
							variant="primary"
							onClick={() => handleSyncTeamLogos(false)}
							disabled={loading}
						>
							{loading ? "Running..." : "Apply Logo Sync"}
						</Button>
					</div>
					{logoSyncSummary && (
						<div className="mt-3">
							<h6>
								Last Sync Summary {logoSyncSummary.dryRun ? "(Dry Run)" : "(Applied)"}
							</h6>
							<Table bordered size="sm" className="mb-2">
								<tbody>
									<tr>
										<td>Matched</td>
										<td>{logoSyncSummary?.counts?.matched ?? 0}</td>
										<td>Updated</td>
										<td>{logoSyncSummary?.counts?.updated ?? 0}</td>
										<td>Unchanged</td>
										<td>{logoSyncSummary?.counts?.unchanged ?? 0}</td>
									</tr>
									<tr>
										<td>Skipped Missing Fields</td>
										<td>{logoSyncSummary?.counts?.skippedMissingFields ?? 0}</td>
										<td>Skipped Ambiguous JSON</td>
										<td>{logoSyncSummary?.counts?.skippedAmbiguousJson ?? 0}</td>
										<td>Skipped Ambiguous DB</td>
										<td>{logoSyncSummary?.counts?.skippedAmbiguousDb ?? 0}</td>
									</tr>
									<tr>
										<td>Unmatched DB Teams</td>
										<td>{logoSyncSummary?.counts?.unmatchedDbTeams ?? 0}</td>
										<td>JSON Total</td>
										<td>{logoSyncSummary?.counts?.jsonTotal ?? 0}</td>
										<td>DB Total</td>
										<td>{logoSyncSummary?.counts?.dbTotal ?? 0}</td>
									</tr>
								</tbody>
							</Table>
							<details>
								<summary>View full sync response</summary>
								<pre
									className="mt-2 p-2 bg-light border rounded"
									style={{ maxHeight: "260px", overflowY: "auto" }}
								>
									{JSON.stringify(logoSyncSummary, null, 2)}
								</pre>
							</details>
						</div>
					)}
				</Card.Body>
			</Card>

			<Card className="mb-4">
				<Card.Body>
					<Card.Title>Create a Team</Card.Title>
					<Form>
						<Form.Group className="mb-3" controlId="teamName">
							<Form.Label>Team Name</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter team name"
								value={newTeam.name}
								onChange={(e) =>
									setNewTeam({ ...newTeam, name: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamShortName">
							<Form.Label>Short Name</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter short name"
								value={newTeam.shortName}
								onChange={(e) =>
									setNewTeam({ ...newTeam, shortName: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamCountry">
							<Form.Label>Country</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter country"
								value={newTeam.country}
								onChange={(e) =>
									setNewTeam({ ...newTeam, country: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamSponsor">
							<Form.Label>Sponsor</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter sponsor"
								value={newTeam.sponsor}
								onChange={(e) =>
									setNewTeam({ ...newTeam, sponsor: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamColor">
							<Form.Label>Color</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter color"
								value={newTeam.color}
								onChange={(e) =>
									setNewTeam({ ...newTeam, color: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamLogoUrl">
							<Form.Label>Logo URL</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter logo URL"
								value={newTeam.logoUrl}
								onChange={(e) =>
									setNewTeam({ ...newTeam, logoUrl: e.target.value })
								}
							/>
						</Form.Group>
						{newTeam.logoUrl && (
							<Form.Group className="mb-3" controlId="teamLogoPreview">
								<Form.Label>Logo Preview</Form.Label>
								<div>
									<img
										src={newTeam.logoUrl}
										alt={`${newTeam.name || "Brand Team"} logo preview`}
										style={{ maxWidth: "100%", maxHeight: "160px" }}
									/>
								</div>
							</Form.Group>
						)}
						<Form.Group className="mb-3" controlId="teamSource">
							<Form.Label>Source</Form.Label>
							<Form.Control
								type="text"
								placeholder="Enter source URL"
								value={newTeam.source}
								onChange={(e) =>
									setNewTeam({ ...newTeam, source: e.target.value })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamIsActive">
							<Form.Check
								type="checkbox"
								label="Is Active"
								checked={newTeam.isActive}
								onChange={(e) =>
									setNewTeam({ ...newTeam, isActive: e.target.checked })
								}
							/>
						</Form.Group>
						<Form.Group className="mb-3" controlId="teamFeatured">
							<Form.Check
								type="checkbox"
								label="Featured"
								checked={newTeam.featured}
								onChange={(e) =>
									setNewTeam({ ...newTeam, featured: e.target.checked })
								}
							/>
						</Form.Group>
						<Button variant="primary" onClick={createTeam}>
							Create
						</Button>
					</Form>
				</Card.Body>
			</Card>

			<Card>
				<Card.Body>
					<Card.Title>Existing Teams</Card.Title>
					<div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
						<Form.Control
							type="text"
							placeholder="Search teams by name, short name, country, or sponsor"
							value={teamSearch}
							onChange={(e) => setTeamSearch(e.target.value)}
							style={{ maxWidth: "480px" }}
						/>
						<div className="text-muted small">
							Showing {filteredTeams.length} of {teams.length} teams
						</div>
					</div>
					<Table striped bordered hover responsive>
						<thead>
							<tr>
								<th>Team Name</th>
								<th>Short Name</th>
								<th>Country</th>
								<th>Sponsor</th>
								<th>Is Active</th>
								<th className="text-nowrap">Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredTeams.map((team) => (
								<tr key={team._id}>
									<td>{team.name}</td>
									<td>{team.shortName}</td>
									<td>{team.country}</td>
									<td>{team.sponsor}</td>
									<td>{team.isActive ? "Yes" : "No"}</td>
									<td className="text-nowrap" style={{ minWidth: "110px" }}>
										<Button
											variant="outline-primary"
											onClick={() => {
												// Handle edit action
												setSelectedTeam(team);
												setShowEditModal(true);
											}}
										>
											Edit
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Card.Body>
			</Card>
			<EditBrandTeamModal
				show={showEditModal}
				onHide={handleCloseEditModal}
				brandTeam={selectedTeam}
				handleUpload={handleUploadImage}
				handleInputChange={handleEditInputChange}
				onSubmit={handleUpdateBrandTeam}
			/>
			</Container>
		</AdminLayout>
	);
};

export default AdminBrandTeamPage;
